import React, { useCallback, useEffect, useState } from "react";

import { AlertTriangle } from "lucide-react";
import { Target } from "lucide-react";
import moment from "moment";
import { toast } from "sonner";
import { Switch } from "../../components/ui/switch.jsx";
import { Label } from "../../components/ui/label.jsx";

// Import shared components
import PageHeader from "../../components/shared/PageHeader";
import Pagination from "../../components/shared/Pagination";
import SearchInput from "../../components/shared/SearchInput";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { useApi } from "../../context/ApiContext";
import { useAuth } from "../../context/AuthContext";
import { useConfirm } from "../../context/ConfirmContext.jsx";
import { usePagination } from "../../hooks/usePagination";
import { getCharCount } from "../../lib/utils";

import LeadFilters from "./component/LeadFilters";
import LeadForm from "./component/LeadForm";
import LeadTable from "./component/LeadTable";
import MeetingAlert from "./component/MeetingAlert";

const LeadManager = () => {
  const { user } = useAuth();
  const api = useApi();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");
  const [showMeetingAlert, setShowMeetingAlert] = useState(false);
  const [upcomingMeetings, setUpcomingMeetings] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("meetingDate");
  const [direction, setDirection] = useState("desc");
  const [showDeleted, setShowDeleted] = useState(false);

  const pagination = usePagination(10);
  const { items: leads, currentPage, pageSize, setPaginationData, setCurrentPage } = pagination;

  const loadLeads = useCallback(
    async (page) => {
      try {
        const response = await api.get(`/leads`, {
          params: {
            page,
            size: pageSize,
            status: filterStatus,
            priority: filterPriority,
            search: searchTerm.trim() || undefined,
            sortBy: sortBy || undefined,
            direction: direction || undefined,
            showDeleted
          }
        });
        setPaginationData(response.data);
      } catch (error) {
        toast.error(error.message || "Failed to load leads");
      }
    },
    [api, filterStatus, filterPriority, searchTerm, pageSize, sortBy, direction, showDeleted, setPaginationData]
  );

  useEffect(() => {
    loadLeads(currentPage);
  }, [currentPage, filterStatus, filterPriority, loadLeads]);

  const [newLead, setNewLead] = useState({
    title: "",
    companyName: "",
    contactPerson: "",
    phone: "",
    email: "",
    status: "new",
    priority: "medium",
    estimatedValue: "",
    nextAction: "",
    meetingDate: "",
    notes: ""
  });

  const isNewLeadNotesOverLimit = getCharCount(newLead.notes || "") > 500;
  const isEditLeadNotesOverLimit = editingLead ? getCharCount(editingLead.notes || "") > 500 : false;

  useEffect(() => {
    if (leads.length > 0) {
      checkUpcomingMeetings(leads);
    }
  }, [leads]);

  const checkUpcomingMeetings = (leadsData) => {
    if (!leadsData) return;
    const now = new Date();
    const upcoming = leadsData.filter((lead) => {
      if (!lead.meetingDate) return false;

      const meetingDate = new Date(lead.meetingDate);
      const timeDiff = meetingDate.getTime() - now.getTime();
      const minutesDiff = timeDiff / (1000 * 60);

      // Show alert if meeting is within next 30 minutes or started within last 5 minutes
      return minutesDiff > -5 && minutesDiff <= 30;
    });

    if (upcoming.length > 0) {
      setUpcomingMeetings(upcoming);
      setShowMeetingAlert(true);
    }
  };

  // Check for upcoming meetings every minute
  useEffect(() => {
    if (leads.length > 0) {
      const interval = setInterval(() => {
        checkUpcomingMeetings(leads);
      }, 60000); // Check every minute

      return () => clearInterval(interval);
    }
  }, [leads]);

  const createLead = async (e) => {
    e.preventDefault();
    if (isNewLeadNotesOverLimit) {
      toast.error("Notes exceed the 500-character limit.");
      return;
    }
    try {
      const formattedDate = newLead.meetingDate ? moment(newLead.meetingDate).format("YYYY-MM-DDTHH:mm:ss") : null;

      await api.post(`/leads`, {
        ...newLead,
        estimatedValue: newLead.estimatedValue ? parseFloat(newLead.estimatedValue) : null,
        meetingDate: formattedDate
      });
      loadLeads(currentPage);
      setNewLead({
        title: "",
        companyName: "",
        contactPerson: "",
        phone: "",
        email: "",
        status: "new",
        priority: "medium",
        estimatedValue: "",
        nextAction: "",
        meetingDate: "",
        notes: ""
      });
      setIsCreateOpen(false);
      toast.success("Lead created successfully!");
    } catch (error) {
      toast.error(error.message || "Failed to create lead");
    }
  };

  const handleUpdateLead = async (e) => {
    e.preventDefault();
    if (isEditLeadNotesOverLimit) {
      toast.error("Notes exceed the 500-character limit.");
      return;
    }
    try {
      const formattedDate = editingLead.meetingDate ? moment(editingLead.meetingDate).format("YYYY-MM-DDTHH:mm:ss") : null;

      await api.put(`/leads/${editingLead.id}`, {
        ...editingLead,
        estimatedValue: editingLead.estimatedValue ? parseFloat(editingLead.estimatedValue) : null,
        meetingDate: formattedDate
      });
      loadLeads(currentPage);
      setEditingLead(null);
      toast.success("Lead updated successfully!");
    } catch (error) {
      toast.error(error.message || "Failed to update lead");
    }
  };

  const updateLeadStatus = async (leadId, status) => {
    try {
      await api.put(
        `/leads/${leadId}/status`,
        { status },
        {
          headers: { "Content-Type": "application/json" }
        }
      );
      loadLeads(currentPage);
      toast.success("Lead status updated!");
    } catch (error) {
      toast.error(error.message || "Failed to update lead status");
    }
  };

  const confirm = useConfirm();

  const deleteLead = async (leadId) => {
    const isConfirmed = await confirm({
      title: "Delete Lead",
      message: "Are you sure you want to delete this lead?"
    });
    if (isConfirmed) {
      try {
        await api.delete(`/leads/${leadId}`);
        loadLeads(currentPage);
        toast.success("Lead deleted successfully!");
      } catch (error) {
        toast.error(error.message || "Failed to delete lead");
      }
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "urgent":
        return "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-500/30";
      case "high":
        return "bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-500/30";
      case "medium":
        return "bg-blue-100 text-yale-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-yale-blue-400 dark:border-yale-blue-500/30";
      case "low":
        return "bg-slate-100 text-yale-blue-700 border-slate-200 dark:bg-yale-blue-800/50 dark:text-slate-400 dark:border-yale-blue-700/50";
      default:
        return "bg-slate-100 text-yale-blue-700 border-slate-200 dark:bg-yale-blue-800/50 dark:text-slate-400 dark:border-yale-blue-700/50";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "new":
        return "bg-blue-100 text-yale-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-yale-blue-400 dark:border-yale-blue-500/30";
      case "contacted":
        return "bg-amber-100 text-yale-blue-700 border-amber-200 dark:bg-amber-900/30 dark:text-yale-blue-400 dark:border-primary/30";
      case "meeting_scheduled":
        return "bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-500/30";
      case "proposal_sent":
        return "bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-500/30";
      case "won":
        return "bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-500/30";
      case "lost":
        return "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-500/30";
      default:
        return "bg-slate-100 text-yale-blue-700 border-slate-200 dark:bg-yale-blue-800/50 dark:text-slate-400 dark:border-yale-blue-700/50";
    }
  };

  if (!user?.permissions?.lead?.read) {
    return (
      <div className="text-center py-12 bg-white dark:bg-yale-blue-900 rounded-xl border border-slate-200 dark:border-yale-blue-800 shadow-sm">
        <div className="text-red-500 mb-4">
          <AlertTriangle className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <h2 className="text-xl font-bold text-yale-blue-900 dark:text-slate-100">Access Denied</h2>
          <p className="text-slate-600 dark:text-slate-400 mt-2">You don't have permission to access Lead Manager.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col animate-in fade-in duration-500">
      <div className="flex-1 space-y-6">
        <PageHeader
          title="Lead Manager"
          subtitle="Track and nurture business opportunities and potential clients"
          actionLabel={user?.permissions?.lead?.create ? "Add New Lead" : null}
          onActionClick={() => setIsCreateOpen(true)}
          icon={Target}
          pageIcon={Target}
          extraActions={
            <div className="flex items-center gap-4">
              <SearchInput
                placeholder="Search leads..."
                className="md:w-72"
                onSearch={(val) => {
                  setSearchTerm(val);
                  setCurrentPage(0);
                }}
              />
            </div>
          }
        />

        <LeadFilters
          filterStatus={filterStatus}
          setFilterStatus={(val) => {
            setFilterStatus(val);
            setCurrentPage(0);
          }}
          filterPriority={filterPriority}
          setFilterPriority={(val) => {
            setFilterPriority(val);
            setCurrentPage(0);
          }}
        />

        <LeadTable
          leads={leads}
          onEdit={setEditingLead}
          onDelete={deleteLead}
          onStatusUpdate={updateLeadStatus}
          getPriorityColor={getPriorityColor}
          getStatusColor={getStatusColor}
          permissions={{
            canEdit: user?.permissions?.lead?.update,
            canDelete: user?.permissions?.lead?.delete
          }}
          sortBy={sortBy}
          direction={direction}
          onSort={(key, dir) => {
            setSortBy(key);
            setDirection(dir);
          }}
          showDeleted={showDeleted}
          setShowDeleted={setShowDeleted}
          setCurrentPage={setCurrentPage}
        />
      </div>

      <div className="mt-8">
        <Pagination pagination={pagination} />
      </div>

      {/* Create Lead Modal */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] p-0 overflow-hidden flex flex-col">
          <div className="p-6 pb-0">
            <DialogHeader>
              <DialogTitle>Add New Lead</DialogTitle>
              <DialogDescription>Create a new lead opportunity</DialogDescription>
            </DialogHeader>
          </div>
          <form onSubmit={createLead} className="flex flex-col flex-1 overflow-hidden">
            <div className="flex-1 overflow-y-auto p-6">
              <LeadForm leadData={newLead} setLeadData={setNewLead} />
            </div>
            <div className="p-6 pt-2 border-t bg-white dark:bg-yale-blue-900 sticky bottom-0 z-10">
              <Button
                type="submit"
                variant="primary"
                className="w-full py-6 rounded-lg shadow-lg"
                disabled={isNewLeadNotesOverLimit}
              >
                Create Lead
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Lead Modal */}
      <Dialog open={!!editingLead} onOpenChange={() => setEditingLead(null)}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] p-0 overflow-hidden flex flex-col">
          <div className="p-6 pb-0">
            <DialogHeader>
              <DialogTitle>Edit Lead</DialogTitle>
              <DialogDescription>Update lead information</DialogDescription>
            </DialogHeader>
          </div>
          {editingLead && (
            <form onSubmit={handleUpdateLead} className="flex flex-col flex-1 overflow-hidden">
              <div className="flex-1 overflow-y-auto p-6">
                <LeadForm leadData={editingLead} setLeadData={setEditingLead} />
              </div>
              <div className="p-6 pt-2 border-t bg-white dark:bg-yale-blue-900 sticky bottom-0 z-10">
                <Button
                  type="submit"
                  variant="primary"
                  className="w-full py-6 rounded-lg shadow-lg"
                  disabled={isEditLeadNotesOverLimit}
                >
                  Update Lead
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <MeetingAlert isOpen={showMeetingAlert} onOpenChange={setShowMeetingAlert} upcomingMeetings={upcomingMeetings} />
    </div>
  );
};

export default LeadManager;
